import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Pencil, TrendingUp, TrendingDown } from 'lucide-react';
import { api } from '../lib/api';
import { todayISO } from '../lib/format';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import CurrencyInput from './CurrencyInput';

export default function ManualEntryDialog({ open, onOpenChange, partnerId, partnerName, onSaved }) {
  const [form, setForm] = useState({ type: 'MANUAL_CREDIT', amount: '', date: todayISO(), description: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setForm({ type: 'MANUAL_CREDIT', amount: '', date: todayISO(), description: '' });
  }, [open]);

  const submit = async () => {
    if (!form.amount || Number(form.amount) <= 0) {
      toast.error('Informe o valor');
      return;
    }
    if (!form.description.trim()) {
      toast.error('Descrição é obrigatória');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/partners/${partnerId}/manual-entries`, {
        type: form.type,
        amount: Number(form.amount),
        date: form.date,
        description: form.description.trim(),
      });
      toast.success('Lançamento manual registrado');
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao registrar');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Pencil className="h-5 w-5" /> Lançamento manual</DialogTitle>
          <p className="text-xs text-muted-foreground">Sócio: <strong>{partnerName}</strong></p>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Tipo *</Label>
            <div className="grid grid-cols-2 gap-2">
              <label className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer ${form.type === 'MANUAL_CREDIT' ? 'border-[hsl(var(--success))] bg-[hsl(var(--success-bg))]' : 'border-border/70'}`}>
                <input type="radio" checked={form.type === 'MANUAL_CREDIT'} onChange={() => setForm((f) => ({ ...f, type: 'MANUAL_CREDIT' }))} className="accent-[hsl(var(--success))]" />
                <TrendingUp className="h-4 w-4 text-[hsl(var(--success))]" />
                <span className="text-sm font-medium">Crédito (+)</span>
              </label>
              <label className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer ${form.type === 'MANUAL_DEBIT' ? 'border-[hsl(var(--danger))] bg-[hsl(var(--danger-bg))]' : 'border-border/70'}`}>
                <input type="radio" checked={form.type === 'MANUAL_DEBIT'} onChange={() => setForm((f) => ({ ...f, type: 'MANUAL_DEBIT' }))} className="accent-[hsl(var(--danger))]" />
                <TrendingDown className="h-4 w-4 text-[hsl(var(--danger))]" />
                <span className="text-sm font-medium">Débito (−)</span>
              </label>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Valor *</Label>
              <CurrencyInput value={form.amount} onChange={(v) => setForm((f) => ({ ...f, amount: v }))} data-testid="manual-amount-input" />
            </div>
            <div>
              <Label>Data</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} />
            </div>
          </div>
          <div>
            <Label>Descrição *</Label>
            <Input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Ex: Ajuste de saldo histórico, aporte avulso..." data-testid="manual-description-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={submitting} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" data-testid="manual-confirm-button">
            {submitting ? 'Salvando...' : 'Confirmar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
